import {
  DSContractListing,
  DSContractState,
} from "@myth-finance/dualstake-ts-sdk";
import { ArrowRight, ClockAlert } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useInterval } from "usehooks-ts";
import { showTimeDiffPartial } from "@/lib/utils";
import { Link } from '@/components/links';
import { useState, useEffect, useMemo } from "react";

function formatFeeBps(v: BigInt | number): string {
  v = Number(v);
  return (v / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function FeeUpgradeCard({
  cState,
  listing,
}: {
  cState: DSContractState;
  listing: DSContractListing;
}) {
  const [delta, setDelta] = useState(0);

  const updateDelta = (cState: DSContractState) => {
    const delta =
      (cState.feeUpdate!.applicableTimestamp * 1000) -
      Date.now();

    setDelta(delta);
  };
  const label = useMemo(() => {
    return showTimeDiffPartial(delta);
  }, [delta]);

  useEffect(() => {
    updateDelta(cState);
  }, [cState]);

  useInterval(() => {
    if (cState && delta > 0) updateDelta(cState);
  }, 1000);

  if (!cState || !listing) return null;
  const noderunnerFeeDelta =
    cState.feeUpdate!.nextNoderunnerFeeBps - Number(cState.noderunnerFeeBps);
  const platformFeeDelta =
    cState.feeUpdate!.nextPlatformFeeBps - Number(cState.platformFeeBps);

  return (
    <Alert variant="destructive" className="max-w-[640px] flex w-fit">
      <ClockAlert strokeWidth={2} className="h-6 w-6 -ml-1" />
      <div className="flex flex-col flex-grow">
        <AlertTitle className="flex-grow font-bold mt-1 leading-2">
          {listing.lstName} fees update is scheduled.
        </AlertTitle>
        <AlertDescription className="flex flex-col">
          <div className="flex flex-col w-fit items-stretch self-center">
            <div className="flex gap-10 items-center justify-between">
              <div>Effective in:</div>
              <div className="flex items-center font-bold gap-1">
                {delta > 0 ? label : "now"}
              </div>
            </div>
            {platformFeeDelta ? (
              <div className="flex gap-10 items-center justify-between">
                <div>Platform fees:</div>
                <div className="flex items-center font-bold gap-1">
                  {formatFeeBps(cState.platformFeeBps)}%{" "}
                  <ArrowRight size={12} strokeWidth={3} />
                  {formatFeeBps(cState.feeUpdate!.nextPlatformFeeBps)}%
                </div>
              </div>
            ) : null}
            {noderunnerFeeDelta ? (
              <div className="flex gap-10 items-center justify-between">
                <div>Node runner fees:</div>
                <div className="flex items-center font-bold gap-1">
                  {formatFeeBps(cState.noderunnerFeeBps)}%{" "}
                  <ArrowRight size={12} strokeWidth={3} />
                  {formatFeeBps(cState.feeUpdate!.nextNoderunnerFeeBps)}%
                </div>
              </div>
            ) : null}
            <Link className="self-end" to="https://docs.myth.finance/dualSTAKE/immutability#fee-configuration">Learn more</Link>
          </div>
        </AlertDescription>
      </div>
    </Alert>
  );
}
